/**
 * Utility functions for managing browser cache and localStorage
 */

/**
 * Clear all Trade Valuation related localStorage data
 */
export const clearAllLocalStorage = (): void => {
  try {
    // Get all keys
    const keys = Object.keys(localStorage);
    
    // Filter for our app-specific keys
    const tradeValuationKeys = keys.filter(key => 
      key.startsWith('trade-') || 
      key.includes('lineup') || 
      key.includes('team') ||
      key.includes('player') ||
      key.includes('selection') ||
      key.includes('cache')
    );
    
    // Remove each key
    tradeValuationKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log(`✅ Cleared ${tradeValuationKeys.length} localStorage items`);
  } catch (error) {
    console.error('❌ Error clearing localStorage:', error);
  }
};

/**
 * Clear specific localStorage keys by pattern
 */
export const clearLocalStorageByPattern = (pattern: string): void => {
  try {
    const keys = Object.keys(localStorage);
    const matchingKeys = keys.filter(key => key.includes(pattern));
    
    matchingKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log(`✅ Cleared ${matchingKeys.length} localStorage items matching "${pattern}"`);
  } catch (error) {
    console.error('❌ Error clearing localStorage by pattern:', error);
  }
};

/**
 * Clear backend rankings cache via API
 * Note: V2 API does not cache rankings, so this is informational only
 */
export const clearBackendCache = async (): Promise<boolean> => {
  try {
    // V2 API does not cache rankings - all calculations are fresh
    console.log('ℹ️ V2 API does not cache rankings. Every calculation is fresh.');
    return true; // Return true since there's nothing to clear
  } catch (error) {
    console.error('❌ Error:', error);
    return false;
  }
};

/**
 * Clear all caches (localStorage + backend)
 */
export const clearAllCaches = async (): Promise<{
  localStorage: boolean;
  backend: boolean;
}> => {
  const results = {
    localStorage: false,
    backend: false,
  };
  
  try {
    // Clear localStorage
    clearAllLocalStorage();
    results.localStorage = true;
    
    // Clear backend cache
    results.backend = await clearBackendCache();
    
    return results;
  } catch (error) {
    console.error('❌ Error clearing all caches:', error);
    return results;
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = (): {
  totalKeys: number;
  tradeValuationKeys: number;
  totalSize: number; // in bytes
} => {
  try {
    const keys = Object.keys(localStorage);
    const tradeKeys = keys.filter(key => 
      key.startsWith('trade-') || 
      key.includes('lineup') || 
      key.includes('team')
    );
    
    let totalSize = 0;
    keys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        totalSize += value.length + key.length;
      }
    });
    
    return {
      totalKeys: keys.length,
      tradeValuationKeys: tradeKeys.length,
      totalSize,
    };
  } catch (error) {
    console.error('❌ Error getting cache stats:', error);
    return {
      totalKeys: 0,
      tradeValuationKeys: 0,
      totalSize: 0,
    };
  }
};

